/**
 * Represents a locale with language and country information.
 */
class Locale {
  private _country: string;
  private _language: string;

  /**
   * Creates a new instance of Locale.
   * @param language - The language code of the locale. Defaults to 'en'.
   * @param country - The country code of the locale. Defaults to 'US'.
   */
  constructor(language = 'en', country = 'US') {
    this._language = language;
    this._country = country;
  }

  /**
   * Gets the country code of the locale.
   * @returns The country code.
   */
  get country(): string {
    return this._country;
  }

  /**
   * Sets the country code of the locale.
   * @param value - The country code to set.
   */
  set country(value: string) {
    this._country = value;
  }

  /**
   * Gets the language code of the locale.
   * @returns The language code.
   */
  get language(): string {
    return this._language;
  }

  /**
   * Sets the language code of the locale.
   * @param value - The language code to set.
   */
  set language(value: string) {
    this._language = value;
  }

  /**
   * Gets the locale string in the format "language-country".
   * @returns The locale string.
   */
  public getLocale(): string {
    return `${this.language}-${this.country.toUpperCase()}`;
  }

  /**
   * Gets the name of the country in the current locale.
   * @returns The country name.
   */
  public getCountryName(): string {
    const regionNames = new Intl.DisplayNames([this.language], {
      type: 'region',
    });
    const countryName = regionNames.of(this.country.toUpperCase());
    return countryName ?? 'Unknown Country';
  }
}

export default Locale;
